import { SoapUtils } from '../../../../utils/SoapUtils';
import { BaseRequest, IBaseRequest } from '../../BaseRequest';

export interface INewMailRequest extends IBaseRequest {
  mail: Mail;
}

export interface Mail {
  identity?: string;
  trackingCode?: string;
  priority?: string;
  group?: string;
  treeHandle?: string;
  parent?: string;
  html?: string;
  encoding?: string;
  messageType?: string;
  mailboxId?: string;
  from?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  notify?: Notify[];
  subject?: string;
  body?: string;
  attachments?: Attachments;
  forwardEmails?: ForwardEmails;
  embedds?: Embedds;
  contextId?: string;
}

function toMailXml(mail: Mail): string {
  let xml = '';
  xml += SoapUtils.makeNode(mail.identity, 'identity');
  xml += SoapUtils.makeNode(mail.trackingCode, 'trackingCode');
  xml += SoapUtils.makeNode(mail.priority, 'priority');
  xml += SoapUtils.makeNode(mail.group, 'group');
  xml += SoapUtils.makeNode(mail.treeHandle, 'treeHandle');
  xml += SoapUtils.makeNode(mail.parent, 'parent');
  xml += SoapUtils.makeNode(mail.html, 'html');
  xml += SoapUtils.makeNode(mail.encoding, 'encoding');
  xml += SoapUtils.makeNode(mail.messageType, 'messageType');
  xml += SoapUtils.makeNode(mail.mailboxId, 'mailboxId');
  xml += SoapUtils.makeNode(mail.from, 'from');
  xml += SoapUtils.makeNode(mail.to, 'to');
  xml += SoapUtils.makeNode(mail.cc, 'cc');
  if (mail.bcc) xml += mail.bcc.map(bcc => `<bcc>${bcc}</bcc>`).join('');
  if (mail.notify) xml += mail.notify.map(notify => toNotifyXml(notify)).join('');
  xml += SoapUtils.makeNode(mail.subject, 'subject');
  if (mail.body) xml += `<body>${mail.body}</body>`;
  if (mail.attachments) xml += toAttachmentsXml(mail.attachments);
  if (mail.forwardEmails) xml += toForwardEmailsXml(mail.forwardEmails);
  if (mail.embedds) xml += toEmbeddsXml(mail.embedds);
  xml += SoapUtils.makeNode(mail.contextId, 'contextId');
  return `<mail>${xml}</mail>`;
}

export interface Attachments {
  attachment: Attachment[];
}

function toAttachmentsXml(attachments: Attachments): string {
  let xml = '';
  attachments.attachment.forEach(attachment => {
    xml += toAttachmentXml(attachment);
  });
  return `<attachments>${xml}</attachments>`;
}

export interface Attachment {
  id: string;
}

function toAttachmentXml(attachment: Attachment): string {
  return `<attachment><documentId>${attachment.id}</documentId></attachment>`;
}

export interface Embedds {
  EMBEDD_DOCLIB: Attachment[];
}

function toEmbeddsXml(embedds: Embedds): string {
  let xml = '';
  embedds.EMBEDD_DOCLIB.forEach(attachment => {
    xml += toAttachmentXml(attachment);
  });
  return `<embedds>${xml}</embedds>`;
}

export interface ForwardEmails {
  forwadEmail: ForwadEmail[];
}

function toForwardEmailsXml(forwardEmails: ForwardEmails): string {
  let xml = '';
  forwardEmails.forwadEmail.forEach(forwadEmail => {
    xml += toForwadEmailXml(forwadEmail);
  });
  return `<forwardEmails>${xml}</forwardEmails>`;
}

export interface ForwadEmail {
  messageId: string;
}

function toForwadEmailXml(forwadEmail: ForwadEmail): string {
  return `<messageId>${forwadEmail.messageId}</messageId>`;
}

export interface Notify {
  contactName?: string;
  id?: string;
}

function toNotifyXml(notify: Notify): string {
  let xml = '';
  if (notify.contactName) xml += `<userName>${notify.contactName}</userName>`;
  if (notify.id) xml += `<userId>${notify.id}</userId>`;
  return `<notify>${xml}</notify>`;
}

export class NewMailRequest extends BaseRequest implements INewMailRequest {
  mail: Mail;

  constructor(server: string, session: string, mail: Mail) {
    super(server, session);
    this.mail = mail;
  }

  public toXml(): string {
    let parameters: string = super.toXml();
    parameters += toMailXml(this.mail);
    return super.wrapperXml('NewMail', parameters);
  }
}
